/**
 * Schémas Zod partagés par tous les modules.
 *
 * Un seul endroit décrit ce qu'est un uuid, une pagination, un montant, une
 * enveloppe d'erreur et — surtout — le **plan d'une représentation**, retour de
 * `GET /representations/:id/plan`, l'endpoint pivot du front (ARCHITECTURE §8).
 *
 * Ces schémas sont la source unique : le backend les branche sur Fastify pour
 * valider/sérialiser, le frontend en dérive ses types TanStack Query.
 */
import { z } from 'zod';
import { ETAT_SIEGE_SERVEUR, MODE_SALLE, ORIGINE_BILLET, PHASE_SAISON, STATUT_REPRESENTATION, } from '../enums.js';
import { CODE_ERREUR } from '../erreurs.js';
/* -------------------------------------------------------------------------- */
/* Primitives                                                                 */
/* -------------------------------------------------------------------------- */
/** Clé primaire : uuid v4 partout (ARCHITECTURE §3). */
export const uuidSchema = z.uuid();
/** Paramètre de route `:id`. */
export const paramsIdSchema = z.object({ id: uuidSchema });
/** Montant en **centimes entiers**, signé (ajustement, solde). Jamais de flottant. */
export const montantSchema = z.int();
/** Montant en centimes entiers, positif ou nul (colonnes `CHECK >= 0`). */
export const montantPositifSchema = z.int().min(0);
/** Montant en centimes entiers, strictement positif (déduction — PAY-02). */
export const montantStrictementPositifSchema = z.int().min(1);
/** Adresse email. Stockée telle quelle ; l'unicité se fait sur `lower(email)`. */
export const emailSchema = z.email().max(320);
/** Libellé court saisi par le personnel (nom de zone, de formule, titre…). */
export const libelleSchema = z.string().trim().min(1).max(200);
/** Texte long optionnel (description de spectacle, notes, texte de billet). */
export const texteLongSchema = z.string().trim().max(5000);
/**
 * Motif — obligatoire partout où la spec l'exige : annulation de billet, de
 * souscription, de représentation, invitation, ajustement (ARCHITECTURE §9.4).
 * Trois caractères minimum : « ok » n'est pas un motif.
 */
export const motifSchema = z.string().trim().min(3).max(500);
/** Couleur d'affichage d'une zone : hexadécimal `#RRGGBB`. */
export const couleurHexSchema = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/u, 'Couleur attendue au format #RRGGBB');
/** Horodatage ISO 8601 en UTC. Stockage `timestamptz`, affichage Europe/Zurich. */
export const dateHeureSchema = z.iso.datetime({ offset: true });
/** Date seule `AAAA-MM-JJ` (dates d'ouverture de campagne — CAM-01). */
export const dateSeuleSchema = z.iso.date();
/** Rang d'un siège : « A », « F », « AA »… (majuscules, court). */
export const rangSchema = z.string().trim().min(1).max(8);
/** Numéro d'un siège dans son rang. */
export const numeroSiegeSchema = z.int().min(1).max(9999);
/** Jeton de QR (billet `B-…` ou carte `C-…`) — I-07, 64 caractères max. */
export const qrTokenSchema = z.string().trim().min(8).max(64);
/* -------------------------------------------------------------------------- */
/* Pagination                                                                 */
/* -------------------------------------------------------------------------- */
export const TAILLE_PAGE_DEFAUT = 50;
export const TAILLE_PAGE_MAX = 500;
/**
 * Query string de pagination. `z.coerce` parce que Fastify livre des chaînes.
 * Les listes du back-office restent petites (NF-01) : pas de curseur.
 */
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    taille: z.coerce.number().int().min(1).max(TAILLE_PAGE_MAX).default(TAILLE_PAGE_DEFAUT),
});
/** Recherche plein texte optionnelle, commune aux listes du back-office. */
export const rechercheQuerySchema = paginationQuerySchema.extend({
    q: z.string().trim().min(1).max(200).optional(),
});
/** Fabrique le schéma d'une page d'éléments : `pageSchema(personneSchema)`. */
export function pageSchema(item) {
    return z.object({
        items: z.array(item),
        total: z.int().min(0),
        page: z.int().min(1),
        taille: z.int().min(1),
    });
}
/** Décalage SQL correspondant à une page (1-indexée). */
export function offsetDePage(pagination) {
    return (pagination.page - 1) * pagination.taille;
}
/* -------------------------------------------------------------------------- */
/* Enveloppe d'erreur (ARCHITECTURE §5)                                       */
/* -------------------------------------------------------------------------- */
export const corpsErreurSchema = z.object({
    code: z.enum(CODE_ERREUR),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
});
/** Corps de TOUTE réponse en erreur de l'API. */
export const enveloppeErreurSchema = z.object({ erreur: corpsErreurSchema });
/** Réponse des mutations sans contenu utile. */
export const okSchema = z.object({ ok: z.literal(true) });
/* -------------------------------------------------------------------------- */
/* Référentiels affichés partout                                              */
/* -------------------------------------------------------------------------- */
/** Zone tarifaire de la salle (parterre, galerie, balcon…). */
export const zoneSchema = z.object({
    id: uuidSchema,
    salleId: uuidSchema,
    nom: libelleSchema,
    couleur: couleurHexSchema,
    ordre: z.int().min(0),
});
/** Catégorie de public (plein, AVS/AI, étudiant, enfant… — CFG-05). */
export const categoriePublicSchema = z.object({
    id: uuidSchema,
    code: z.string().trim().min(1).max(50),
    libelle: libelleSchema,
    actif: z.boolean(),
    ordre: z.int().min(0),
});
/* -------------------------------------------------------------------------- */
/* Plan d'une représentation — GET /representations/:id/plan                  */
/* -------------------------------------------------------------------------- */
/** Occupant connu d'un siège, pour l'infobulle du plan. */
export const occupantSiegeSchema = z.object({
    personneId: uuidSchema.nullable(),
    /** « Dubuis Marie », ou « Vente anonyme » / « Invitation presse » à défaut. */
    nomAffiche: z.string().max(200),
});
/**
 * Un siège **avec son état pour la représentation demandée**.
 * C'est l'unité que consomme `components/SeatMap` (ARCHITECTURE §9.3).
 * `etat` ne contient jamais `selectionne` : la sélection est un état client.
 */
export const ACCESSIBILITE_PLACE = ['standard', 'pmr', 'accompagnant'];
export const LIBELLES_ACCESSIBILITE_PLACE = {
    standard: 'Place standard',
    pmr: 'Emplacement PMR',
    accompagnant: 'Place accompagnant',
};
export const siegeEtatSchema = z.object({
    id: uuidSchema,
    rang: rangSchema,
    numero: numeroSiegeSchema,
    /** Position sur la grille de rendu SVG, en unités de plan (pas en pixels). */
    posX: z.int(),
    posY: z.int(),
    zoneId: uuidSchema,
    /** `false` = hors vente (CFG-03) : aucun billet possible (I-06). */
    vendable: z.boolean(),
    accessibilite: z.enum(ACCESSIBILITE_PLACE).optional(),
    siegePmrId: uuidSchema.nullable().optional(),
    noteHorsVente: z.string().max(200).nullable(),
    etat: z.enum(ETAT_SIEGE_SERVEUR),
    /** Renseignés quand `etat` vaut vendu / abonnement / invitation. */
    billetId: uuidSchema.nullable(),
    origine: z.enum(ORIGINE_BILLET).nullable(),
    occupant: occupantSiegeSchema.nullable(),
    /** Renseigné quand `etat === 'gele'` (CAM-04) : la proposition qui le retient. */
    propositionId: uuidSchema.nullable(),
    /**
     * Renseigné quand `etat === 'reserve_abonnement'` (SOU-01) : la souscription
     * série qui a déjà promis cette place. Symétrique de `propositionId`.
     *
     * L'écran de souscription en a besoin pour distinguer « promise à quelqu'un
     * d'autre » de « promise par le brouillon que je suis en train de corriger » :
     * sans lui, un guichetier qui reprend son propre brouillon après un conflit
     * verrait ses propres sièges déclarés indisponibles.
     */
    souscriptionId: uuidSchema.nullable(),
    /**
     * Renseigné quand `etat === 'retenu'` (I-08, REZ-02) : l'instant où le panier
     * qui tient la place expire. Le front en fait un compte à rebours, et l'écran
     * de déblocage de l'administrateur y lit « retenu jusqu'à 14:37 » (DEB-01).
     */
    retenuJusquA: dateHeureSchema.nullable(),
});
/**
 * Tarif applicable, résolu par le serveur pour cette représentation.
 * `zoneId === null` signifie « toutes zones » (§6.3).
 */
export const tarifPlanSchema = z.object({
    zoneId: uuidSchema.nullable(),
    categoriePublicId: uuidSchema,
    /** Centimes. Toujours 0 quand `nonVendu`. */
    montant: montantPositifSchema,
    /**
     * ★ TAR-04 bis — cette catégorie n'est PAS vendue ici, et c'est voulu.
     *
     * Optionnel dans le schéma, et c'est délibéré : front et back sont deux
     * images déployées séparément, et un plan servi par un back qui n'a pas
     * encore la colonne ne doit pas faire échouer la validation côté client.
     * Absent se lit comme `false` — l'ancien comportement, exactement.
     */
    nonVendu: z.boolean().optional(),
});
/** Compteurs par état, pour l'en-tête du guichet et DASH-01. */
export const totauxPlanSchema = z.object({
    total: z.int().min(0),
    libre: z.int().min(0),
    vendu: z.int().min(0),
    abonnement: z.int().min(0),
    invitation: z.int().min(0),
    horsVente: z.int().min(0),
    gele: z.int().min(0),
    /** SOU-01 — places promises à un abonné, billets pas encore émis. */
    reserveAbonnement: z.int().min(0),
    /**
     * I-08 / REZ-01 — places retenues par le panier d'un AUTRE visiteur, le temps
     * de son paiement. Compteur volatil par nature : il fond tout seul quand les
     * paniers expirent (REZ-04). Sans lui, la somme des seaux ne ferait plus le
     * total de la salle dès qu'une vente en ligne est en cours.
     */
    retenu: z.int().min(0),
});
/** Contexte minimal de la représentation, pour l'en-tête de l'écran. */
export const representationPlanSchema = z.object({
    id: uuidSchema,
    dateHeure: dateHeureSchema,
    statut: z.enum(STATUT_REPRESENTATION),
    spectacleId: uuidSchema,
    spectacleTitre: libelleSchema,
    horsAbonnement: z.boolean(),
    saisonId: uuidSchema,
    saisonLibelle: libelleSchema,
    /**
     * ★ 0012 — OÙ l'on joue ce soir (D7 renversée). Porté par l'en-tête et non
     * seulement par `PlanRepresentation.salle`, parce que l'agenda du guichet
     * réutilise cet en-tête SANS le plan : sans ces deux champs, Luc choisissait
     * sa date dans une liste où « 12 libres sur 420 » et « 12 libres sur 90 »
     * s'écrivaient exactement pareil.
     */
    salleId: uuidSchema,
    salleNom: libelleSchema,
    /** Détermine si la vente à l'unité est ouverte (VEN-02). */
    phaseSaison: z.enum(PHASE_SAISON),
});
/**
 * Retour de `GET /representations/:id/plan` — tous les sièges avec leur état,
 * les zones, les catégories de public et les tarifs applicables.
 * Sert au guichet (VEN-01), aux souscriptions (SOU-01/03/04) et à la campagne
 * (CAM-05). Un seul appel : le front ne recompose rien.
 */
export const planRepresentationSchema = z.object({
    representation: representationPlanSchema,
    salle: z.object({
        id: uuidSchema,
        nom: libelleSchema,
        /**
         * ★ LIB-03 — au guichet comme dans la boutique, un lieu à placement libre
         * n'a pas de plan : `sieges` est vide et l'on y saisit une quantité. Voir
         * le commentaire détaillé de `planPublicSchema.salle.mode` (`boutique.ts`).
         */
        mode: z.enum(MODE_SALLE),
    }),
    zones: z.array(zoneSchema),
    categoriesPublic: z.array(categoriePublicSchema),
    tarifs: z.array(tarifPlanSchema),
    sieges: z.array(siegeEtatSchema),
    totaux: totauxPlanSchema,
});
/* -------------------------------------------------------------------------- */
/* Petits utilitaires de domaine, partagés back/front                         */
/* -------------------------------------------------------------------------- */
/**
 * ★★ LIB-04 — LE rang conventionnel des sièges d'un lieu à placement libre.
 *
 * Les 200 sièges d'un lieu de jauge 200 sont de vraies lignes de `siege` (c'est
 * toute l'astuce du chantier Q : le siège devient une unité comptable, et les
 * neuf invariants tiennent sans être touchés). Mais leur rang et leur numéro ne
 * signifient RIEN : personne ne s'assied « au rang LIBRE, place 137 ».
 *
 * Le vocabulaire est donc porté par la donnée elle-même, pas par un drapeau
 * qu'il faudrait charrier jusqu'aux quarante appels de `libelleSiege` du
 * dépôt — courriels, PDF, verdicts de scan, portail abonné, tableaux de bord.
 * Un siège dont le rang vaut cette constante s'imprime « Entrée », partout,
 * **par construction** : c'est la même doctrine que `ETAT_SIEGE_PUBLIC`, où
 * ce qui n'existe pas dans le type ne peut pas fuir.
 *
 * ⚠️ Corollaire tenu par `rangSchema` et par le service de configuration : une
 * salle **numérotée** ne peut pas nommer un rang ainsi. Sans ce refus, un
 * théâtre facétieux baptiserait son balcon « LIBRE » et ses billets diraient
 * « Entrée » — le sentinel doit être inatteignable autrement que par la
 * création d'un lieu à placement libre.
 */
export const RANG_PLACEMENT_LIBRE = 'LIBRE';
/** LIB-04 — ce que porte un billet de lieu à placement libre, au lieu du siège. */
export const LIBELLE_PLACE_LIBRE = 'Entrée';
/** `true` si ce siège est une unité comptable d'un lieu à placement libre. */
export function estPlaceLibre(siege) {
    return siege.rang === RANG_PLACEMENT_LIBRE;
}
/**
 * Rang **saisi par un humain** dans la configuration d'une salle numérotée.
 *
 * Identique à `rangSchema` (qui reste le schéma de SORTIE, et doit donc laisser
 * passer le sentinel), à un refus près : le rang réservé aux lieux à placement
 * libre. C'est ce refus qui rend `libelleSiege` digne de confiance — sans lui,
 * « Entrée » pourrait s'imprimer sur le billet d'un fauteuil numéroté.
 */
export const rangSaisiSchema = rangSchema.refine((v) => v !== RANG_PLACEMENT_LIBRE, {
    message: `« ${RANG_PLACEMENT_LIBRE} » est réservé aux lieux à placement libre : choisissez un autre nom de rang.`,
});
/**
 * Libellé humain d'un siège : `('F', 12)` → `« F12 »`. Un seul format partout.
 *
 * ★ LIB-04 — sauf pour un lieu à placement libre, où il n'y a pas d'emplacement
 * à nommer : le billet, le courriel, le PDF et le verdict de contrôle disent
 * tous « Entrée ».
 */
export function libelleSiege(siege) {
    if (estPlaceLibre(siege))
        return LIBELLE_PLACE_LIBRE;
    return `${siege.rang}${String(siege.numero)}`;
}
/**
 * ★★ LIB-04 — LA PLACE EN TOUTES LETTRES, telle qu'un écran, un courriel ou un
 * PDF la NOMME : « Fauteuil F12 » sur un lieu numéroté, « Entrée » sur un lieu
 * à placement libre.
 *
 * `libelleSiege` rend l'IDENTIFIANT de la place (« F12 ») ; celle-ci rend la
 * PHRASE. La distinction n'est pas cosmétique : une douzaine d'écrans
 * écrivaient `` `Fauteuil ${ligne.siege}` `` à la main, et le jour où
 * `libelleSiege` s'est mis à dire « Entrée », ils ont tous imprimé « Fauteuil
 * Entrée » à un client payant. Une interpolation locale est un point de vérité
 * de plus, donc une occasion de plus de mentir.
 *
 * Accepte indifféremment le siège (`{rang, numero}`) ou son libellé déjà
 * composé par le serveur — le panier, la confirmation et les documents ne font
 * voyager que le second, et rien n'oblige un écran à retrouver le premier.
 */
export function designationPlace(place) {
    const libelle = typeof place === 'string' ? place : libelleSiege(place);
    return libelle === LIBELLE_PLACE_LIBRE ? LIBELLE_PLACE_LIBRE : `Fauteuil ${libelle}`;
}
/**
 * ★ LIB-04 — l'INTITULÉ du champ qui porte la place, sur un billet ou une
 * fiche : « Siège » sur un lieu numéroté, « Place » sur un lieu à placement
 * libre. « Siège : Entrée » serait déjà un demi-mensonge — il n'y a pas de
 * siège attribué, et c'est précisément ce que le spectateur doit comprendre
 * avant d'arriver à la porte.
 */
export function intitulePlace(place) {
    const libelle = typeof place === 'string' ? place : libelleSiege(place);
    return libelle === LIBELLE_PLACE_LIBRE ? 'Place' : 'Siège';
}
/**
 * ★ LIB-03 — « ce libellé désigne-t-il une entrée de lieu à placement libre ? »
 *
 * Le pendant de `estPlaceLibre` pour les écrans qui ne reçoivent QUE le
 * libellé. C'est légitime, et voulu : le vocabulaire est porté par la donnée
 * elle-même (voir `RANG_PLACEMENT_LIBRE`), et une salle numérotée ne peut pas
 * nommer un rang « LIBRE » — `rangSaisiSchema` le refuse. Un écran peut donc
 * en déduire qu'il n'y a **pas de plan à proposer**, sans qu'on charrie un
 * drapeau de plus dans chaque DTO.
 */
export function estLibellePlaceLibre(libelle) {
    return libelle === LIBELLE_PLACE_LIBRE;
}
/**
 * ★★ LIB-05 — CE QUE LE CONTRÔLEUR LIT DANS UN VERDICT, en ligne comme hors
 * ligne.
 *
 * Dans une salle numérotée, la bonne information est le fauteuil : le
 * contrôleur oriente la personne (« F12, rang du fond »), et c'est ce qui
 * distingue deux billets par ailleurs identiques.
 *
 * Dans un lieu à placement libre, il n'y a pas de fauteuil vers lequel
 * orienter, et « siège Entrée » ne veut rien dire. La désignation utile devient
 * **le nom et la catégorie** : ce sont eux qui disent au contrôleur qui il a
 * devant lui et à quel tarif il est entré (un tarif enfant sur un adulte, c'est
 * la seule vérification qui reste possible à la porte).
 *
 * ★ **Pourquoi la phrase vit ici et non dans le service de scan.** Il y a DEUX
 * moteurs de verdict : celui du serveur, et celui qui tourne dans le navigateur
 * du contrôleur quand le réseau tombe (HLG). Ils rendent les mêmes objets et
 * doivent rendre les mêmes phrases — sinon un contrôleur lit « Entrée — Claire
 * Roulet, Plein tarif » avec du réseau et « siège Entrée » sans, pour le même
 * billet, à trente secondes d'intervalle. Deux implémentations d'une même
 * phrase, c'est une qui finira par dériver.
 */
export function designationEntreeScan(entree) {
    if (estLibellePlaceLibre(entree.siegeLibelle)) {
        return `${LIBELLE_PLACE_LIBRE} — ${entree.nomAffiche}, ${entree.categorieLibelle}`;
    }
    return `siège ${entree.siegeLibelle}, ${entree.zoneNom}`;
}
/** Tri naturel des sièges : par rang, puis par numéro croissant. */
export function comparerSieges(a, b) {
    const parRang = a.rang.localeCompare(b.rang, 'fr', { numeric: true });
    return parRang !== 0 ? parRang : a.numero - b.numero;
}
/**
 * Résout une case (zone × catégorie) dans la liste renvoyée par le plan : une
 * ligne propre à la zone l'emporte sur une ligne « toutes zones ».
 *
 * ⚠️ La priorité de zone joue AVANT l'état : une case fermée propre à la zone
 * l'emporte sur un prix « toutes zones », et réciproquement. C'est la seule
 * lecture cohérente — sinon fermer une seule zone serait impossible, ou
 * fermerait tout.
 */
export function resoudreCaseTarif(tarifs, zoneId, categoriePublicId) {
    let toutesZones = null;
    for (const tarif of tarifs) {
        if (tarif.categoriePublicId !== categoriePublicId)
            continue;
        if (tarif.zoneId === zoneId)
            return etatDe(tarif);
        if (tarif.zoneId === null)
            toutesZones = tarif;
    }
    return toutesZones === null ? { etat: 'absent' } : etatDe(toutesZones);
}
function etatDe(tarif) {
    /* `?? false` : un back qui n'a pas encore la colonne omet le champ, et
       l'absence doit se lire comme « vendu », l'ancien comportement. */
    return (tarif.nonVendu ?? false)
        ? { etat: 'non_vendu' }
        : { etat: 'tarife', montant: tarif.montant };
}
/**
 * Résout le tarif d'une case (zone × catégorie) dans la liste renvoyée par le
 * plan : une ligne propre à la zone l'emporte sur une ligne « toutes zones ».
 *
 * Renvoie `null` si la case est absente — TAR-04 : l'appelant **bloque** alors
 * l'opération avec `AppError.tarifManquant`, il ne facture jamais 0.– .
 *
 * ⚠️ Renvoie AUSSI `null` sur une case délibérément fermée : sans prix, il n'y
 * a rien à facturer. Les appelants qui doivent DIRE la différence — le guichet,
 * qui accuserait sinon une faute de saisie, et le contrôle de complétude, qui
 * refuserait sinon d'ouvrir la saison — passent par `resoudreCaseTarif`.
 */
export function resoudreTarif(tarifs, zoneId, categoriePublicId) {
    const cas = resoudreCaseTarif(tarifs, zoneId, categoriePublicId);
    return cas.etat === 'tarife' ? cas.montant : null;
}
/** Un siège est-il sélectionnable au guichet ? (libre, vendable, non gelé) */
export function siegeSelectionnable(siege) {
    return siege.vendable && siege.etat === 'libre';
}
/** Natures de formule pour lesquelles des sièges sont choisis à la souscription. */
export const NATURES_AVEC_SIEGES_A_LA_SOUSCRIPTION = ['serie'];
//# sourceMappingURL=commun.js.map